import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { isMaster, podeGravarCadastroAdmin } from '../../../utils/perfil';
import { Num, PillStatus } from '../../../components/gps';
import PainelLista, { Coluna } from '../../../components/gps/PainelLista';
import MenuAcoes, { ResumoExclusao } from '../../../components/gps/MenuAcoes';
import Modal, { BotaoSecundario } from '../../../components/gps/Modal';
import ModalGrupo from '../../../components/gps/ModalGrupo';
import FiltroRegional from '../../../components/gps/FiltroRegional';
import { linhasIniciais } from '../../../components/gps/paginacao';

/* ============================================================================
   Grupos (entidades) - visual novo (shadcn + Tailwind).

   Rota paralela: a tela antiga em /dashboard/entidades continua no ar. O menu
   passa a apontar para ca; voltar atras e uma linha no NavConfig.

   SOMENTE LEITURA. Criar e editar continuam nas paginas existentes
   (/dashboard/entidades/new/:codigo), que nao foram tocadas.

   Contrato conferido em remoteAPI/src/app/entidades/entidades.service.ts
   (paginadoPrincipal, linhas 221-261):
     GET /entidades/paginadoPrincipal?page&rows&valor&campo&direcao&ordenar&status&regional
     -> content: [{ nome, codigo, status, descricao, regional, createdAt,
                    dispositivosCount, totalCount }]
        totalElements: content[0].totalCount

   DUAS ARMADILHAS DESTE ENDPOINT, ambas verificadas no SQL:

   1. Ele filtra `grupo.STATUS = 1` FIXO no WHERE. O parametro `status` da query
      string e ACEITO e IGNORADO. Por isso esta tela NAO tem filtro de status:
      um segmentado "Inativos" devolveria a mesma lista de ativos, que e pior que
      nao existir. Os numeros de ativo/inativo aparecem nos indicadores, que vem
      de /entidades/findByStatus e sao reais.

   2. `valor` e `campo` sao interpolados em SQL BRUTO (`${tabela} Like '%${valor}%'`).
      `campo` so pode sair das constantes daqui. Para `valor`, que e digitado, a
      correcao de verdade e no backend (parametro vinculado); enquanto isso, tirar
      a aspa simples aqui evita que uma busca comum quebre a consulta.
   ============================================================================ */

type Grupo = {
  codigo?: number;
  nome?: string;
  descricao?: string;
  regional?: string;
  status?: number | string;
  dispositivosCount?: number;
};


function numero(valor: unknown) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

function ehAtivo(v: unknown) {
  return String(v).toUpperCase() === 'ATIVO' || Number(v) === 1;
}

Grupos.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function Grupos() {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [menuAberto, setMenuAberto] = useState<number | null>(null);
  const [apagando, setApagando] = useState<Grupo | null>(null);
  const [removendo, setRemovendo] = useState(false);
  // `undefined` = fechado; '0' = criar; qualquer outro = editar aquele codigo.
  const [editando, setEditando] = useState<string | undefined>(undefined);

  /* `?editar=<codigo>` na URL abre o modal do grupo direto.
     Existe porque outras telas LINKAM para um grupo especifico - o detalhe do
     dispositivo ("Grupo: 0003 - INDIRETO - TO") e o modal de politica. Esses links
     apontavam para `/dashboard/entidades/new/:id`, a tela antiga em MUI; sem uma
     forma de pedir "abre a lista nova com este grupo", a alternativa seria deixar
     os links levando para a tela velha.
     A URL e limpa depois de abrir (`replace`, sem entrada nova no historico): assim
     o Voltar do navegador nao reabre o modal, e recarregar a pagina nao insiste em
     abrir um cadastro que a pessoa acabou de fechar. */
  useEffect(() => {
    const alvo = router.query?.editar;
    if (!alvo) return;
    setEditando(String(Array.isArray(alvo) ? alvo[0] : alvo));
    // Um argumento so: o shim de next/router deste projeto (src/compat) mapeia
    // `replace` para o navigate do react-router e IGNORA `as`/`options` - passar
    // `{ shallow: true }` daria a impressao de um comportamento que nao existe aqui.
    router.replace('/dashboard/entidades-novo');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query?.editar]);
  const [linhas, setLinhas] = useState<Grupo[]>([]);
  const [total, setTotal] = useState(0);
  // Recarrega os cartoes (Total/Ativos) depois de apagar ou salvar - antes so na montagem.
  const [versaoIndices, setVersaoIndices] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [busca, setBusca] = useState('');
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais);
  const [regional, setRegional] = useState<number | null>(null);
  // Campos aceitos pelo backend: viram `grupo.${ordenar}` no SQL de
  // paginadoPrincipal, mais os dois casos especiais (dispositivosCount, regional).
  const [ordenarPor, setOrdenarPor] = useState('nome');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('asc');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [indices, setIndices] = useState({ total: 0, ativos: 0, inativos: 0 });

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      // Uma caixa so, resolvida pelo formato: texto com hifen/underscore longo
      // costuma ser DEVICE_ID; o resto e nome. Evita o seletor de campo da tela
      // antiga sem perder a busca por aparelho, que e o caso real do suporte.
      const termo = busca.trim();
      /* DEVICE_ID do Android Management e hexadecimal (ex.: 39f30868b8cadc0e) -
         mesmo criterio da tela de Dispositivos. A regra antiga (`[A-Za-z0-9._-]{8,}`)
         mandava "INDIRETO", "GOIANIA1" ou "CR-79667" buscar por aparelho e
         devolvia "Nenhum grupo encontrado". */
      const pareceDeviceId = /^[0-9a-f]{12,20}$/i.test(termo) && /\d/.test(termo) && /[a-f]/i.test(termo);
      const campo = pareceDeviceId ? 'deviceId' : 'nome';

      const resposta = await axios.get<{ content: Grupo[]; totalElements: number }>(
        `/entidades/paginadoPrincipal?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(termo.replace(/'/g, ''))}&campo=${campo}` +
          `&direcao=${direcao}&ordenar=${ordenarPor}&status=1&regional=${regional ?? 'null'}`
      );
      setLinhas(resposta.data?.content || []);
      setTotal(numero(resposta.data?.totalElements));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar os grupos.');
      setLinhas([]);
      setTotal(0); // sem isto o rodape seguia "Mostrando 0 de N" com a paginacao ativa
    } finally {
      setCarregando(false);
    }
  }, [pagina, busca, linhasPorPagina, regional, ordenarPor, direcao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    axios
      .get<any>('/entidades/findByStatus')
      .then((r) => {
        const d = r.data || {};
        setIndices({ total: numero(d.total), ativos: numero(d.ativo), inativos: numero(d.inativo) });
      })
      .catch(() => setIndices({ total: 0, ativos: 0, inativos: 0 }));
  }, [versaoIndices]);

  const colunas: Array<Coluna<Grupo>> = useMemo(
    () => [
      {
        chave: 'nome',
        ordenarPor: 'nome',
        exportar: (g) => g.nome ?? '',
        rotulo: 'Grupo',
        largura: 'minmax(220px,2.4fr)',
        esqueleto: 'w-44',
        /* SEM A SIGLA DE INICIAIS na frente do nome.
           O `AvatarIniciais` monta duas letras a partir do texto, e serve quando o
           texto e NOME DE PESSOA - ali a inicial ajuda a achar na lista. Nome de
           grupo aqui e codigo ("0003 - INDIRETO - TO", "[CR-79667]-[P-67]"), entao
           a sigla saia como "01", "C7", "1G": ruido que ainda por cima repete o
           inicio do proprio texto ao lado. */
        render: (g) => <span className="block truncate text-[12.5px] font-semibold">{g.nome || ' - '}</span>,
      },
      {
        chave: 'descricao',
        ordenarPor: 'descricao',
        exportar: (g) => g.descricao ?? '',
        rotulo: 'Descrição',
        largura: 'minmax(180px,2fr)',
        esqueleto: 'w-48',
        render: (g) => (
          <span className="block truncate text-[12px] text-muted-foreground">{g.descricao || '—'}</span>
        ),
      },
      {
        chave: 'regional',
        ordenarPor: 'regional',
        exportar: (g) => g.regional ?? '',
        rotulo: 'Regional',
        largura: 'minmax(140px,1.2fr)',
        esqueleto: 'w-28',
        render: (g) => <span className="block truncate text-[12px]">{g.regional || '—'}</span>,
      },
      {
        chave: 'dispositivos',
        ordenarPor: 'dispositivosCount',
        exportar: (g) => numero(g.dispositivosCount),
        rotulo: 'Dispositivos',
        largura: '130px',
        alinhar: 'dir',
        esqueleto: 'w-10',
        render: (g) => (
          <Num className="text-[12px] font-medium">
            {numero(g.dispositivosCount).toLocaleString('pt-BR')}
          </Num>
        ),
      },
      {
        chave: 'status',
        exportar: (g) => (ehAtivo(g.status) ? 'ATIVO' : 'INATIVO'),
        rotulo: 'Status',
        largura: '110px',
        esqueleto: 'w-16',
        render: (g) => <PillStatus ativo={ehAtivo(g.status)} />,
      },
      {
        chave: 'acao',
        rotulo: 'Opções',
        largura: '80px',
        alinhar: 'dir',
        esqueleto: 'nenhum',
        // As mesmas acoes do menu da tela antiga. "Visualizar" e "Editar"
        // apontavam para a MESMA rota (entidades/index.tsx:184 e :191), entao
        // viraram um item - dois itens identicos nao sao duas funcoes.
        render: (g) => (
          <MenuAcoes<Grupo>
            linha={g}
            aberto={menuAberto === g.codigo}
            onAlternar={() => setMenuAberto(menuAberto === g.codigo ? null : g.codigo ?? null)}
            onFechar={() => setMenuAberto(null)}
            ancorarADireita
            acoes={[
              {
                rotulo: 'Abrir / editar',
                ao: (x) => x.codigo && setEditando(String(x.codigo)),
              },
              {
                rotulo: 'Dispositivos do grupo',
                ao: (x) => x.codigo && router.push(`/dashboard/dispositivos-novo/${x.codigo}`),
              },
              {
                rotulo: 'Apagar',
                perigo: true,
                /* SO MASTER (10/09/2026, a pedido).

                   Estava em `podeGravarCadastroAdmin`, que inclui Administrador - o
                   mesmo teste usado para CRIAR e EDITAR. Apagar nao pertence a essa
                   categoria: criar errado se corrige editando, apagar nao tem desfazer
                   e leva junto os vinculos do registro.

                   A autoridade continua sendo o backend; isto so evita mostrar uma
                   opcao que a pessoa nao deveria acionar. */
                visivel: isMaster(user),
                ao: (x) => setApagando(x),
              },
            ]}
          />
        ),
      },
    ],
    [menuAberto, router, user]
  );

  const apagar = async () => {
    if (!apagando?.codigo) return;
    setRemovendo(true);
    try {
      await axios.delete(`/entidades/${apagando.codigo}`);
      enqueueSnackbar('Grupo apagado.', { variant: 'success' });
      setApagando(null);
      setVersaoIndices((v) => v + 1);
      // Era o unico item de uma pagina que nao e a primeira: volta uma pagina.
      // Sem isto a tela ficava em "Nenhum ... encontrado" com registros antes.
      if (linhas.length === 1 && pagina > 0) setPagina((p) => p - 1);
      else await carregar();
    } catch (e: any) {
      // O backend recusa apagar grupo com dispositivo vinculado e explica na
      // mensagem - repassamos em vez de um erro genérico.
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível apagar o grupo.', { variant: 'error' });
    } finally {
      setRemovendo(false);
    }
  };

  return (
    <Page title="Grupos">
      <PainelLista<Grupo>
        titulo="Grupos"
        descricao={
          <>
            <Num>{(indices.total || total).toLocaleString('pt-BR')}</Num> grupos cadastrados. A listagem
            mostra apenas grupos ativos — é assim que o endpoint responde, e os inativos aparecem no
            indicador ao lado.
          </>
        }
        acao={
          <button
            type="button"
            onClick={() => setEditando('0')}
            className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            + Novo grupo
          </button>
        }
        indicadores={[
          { rotulo: 'Total', valor: indices.total || total, legenda: 'cadastrados', tom: 'info' },
          {
            rotulo: 'Ativos',
            valor: indices.ativos,
            legenda: 'em operação',
            tom: 'ok',
            base: indices.total || total,
          },
          {
            rotulo: 'Inativos',
            valor: indices.inativos,
            legenda: 'revisar',
            tom: 'danger',
            base: indices.total || total,
          },
        ]}
        busca={busca}
        aoBuscar={(v) => {
          setPagina(0);
          setBusca(v);
        }}
        placeholderBusca="Nome do grupo ou DEVICE_ID"
        filtrosExtras={<FiltroRegional valor={regional} onChange={(v) => { setPagina(0); setRegional(v); }} />}
        ordenarPor={ordenarPor}
        direcao={direcao}
        aoOrdenar={(campo) => {
          if (campo === ordenarPor) setDirecao((v) => (v === 'asc' ? 'desc' : 'asc'));
          else { setOrdenarPor(campo); setDirecao('asc'); }
          setPagina(0);
        }}
        linhasPorPagina={linhasPorPagina}
        aoTrocarLinhasPorPagina={(n) => { setPagina(0); setLinhasPorPagina(n); }}
        aoLimparFiltros={() => { setBusca(''); setRegional(null); setOrdenarPor('nome'); setDirecao('asc'); setPagina(0); }}
        nomeArquivo="grupos"
        colunas={colunas}
        linhas={linhas}
        chaveLinha={(g, i) => String(g.codigo ?? `sem-codigo-${i}`)}
        /* 🔴 CORRIGIDO: o clique na linha levava para `/dashboard/entidades/new/:id`,
           a TELA ANTIGA em MUI - a mesma lista tinha "Abrir / editar" no menu
           abrindo o modal novo e a linha inteira abrindo a tela velha. O modal
           (`ModalGrupo`, ali embaixo) ja existia e ja estava montado nesta pagina;
           faltava a linha usar ele. */
        aoClicarLinha={(g) => g.codigo && setEditando(String(g.codigo))}
        carregando={carregando}
        erro={erro}
        aoRecarregar={carregar}
        vazio="Nenhum grupo encontrado com esse filtro."
        pagina={pagina}
        totalPaginas={Math.max(1, Math.ceil(total / linhasPorPagina))}
        total={total}
        aoTrocarPagina={setPagina}
        rotuloPlural="grupos"
      />

      <ModalGrupo
        aberto={editando !== undefined}
        codigo={editando}
        onFechar={() => setEditando(undefined)}
        onSalvo={() => { setVersaoIndices((v) => v + 1); carregar(); }}
      />

      <Modal
        aberto={Boolean(apagando)}
        titulo="Apagar grupo?"
        descricao="Grupo com dispositivo vinculado é recusado pelo servidor."
        icone="!"
        largura="max-w-[460px]"
        onFechar={() => setApagando(null)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setApagando(null)} disabled={removendo}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={apagar}
              disabled={removendo}
              className="h-9 rounded-lg bg-danger px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {removendo ? 'Apagando...' : 'Apagar grupo'}
            </button>
          </>
        }
      >
        {apagando && (
          <ResumoExclusao
            titulo={apagando.nome || '—'}
            detalhe={apagando.codigo ? `código ${apagando.codigo}` : undefined}
            extra={
              <>
                Dispositivos vinculados:{' '}
                <strong className="text-ink">{numero(apagando.dispositivosCount)}</strong>
              </>
            }
          />
        )}
      </Modal>
    </Page>
  );
}
